<template>
  <div class="content-wrapper">
    <div class="content">
      <div
        class="rid-missing"
        v-for="(link, i) in preReleasesWithoutRid"
        :key="i"
      >
        <div class="text">
          <div class="title">
            <i class="fa fa-exclamation-circle"></i>
            Pre-release issue: {{ link.release_name }}
          </div>
          <div class="details">
            Add an ISRC/UPC to add the release to your fans' libraries on release day.
          </div>
        </div>
        <div class="button-container">
          <button
            @click="$router.push({ name: 'SmartLink', params: { id: link.id }, query: { action: 'edit' } })"
            class="button-default big"
          >
            <span>Update</span>
          </button>
        </div>
      </div>
      <div class="title-text">
        <p class="title">Smart Links</p>
        <p class="subtitle">
          Manage and create your bio link, and pages for your songs, albums,
          podcasts, and playlists.
        </p>
      </div>
      <div class="content-body">
        <!-- Rest of the content -->
        <template v-if="sortedLinks.length">
          <div>
            <div class="chart-date-picker">
              <date-range-picker
                :dateRange="dateRange"
                :linkedCalendars="false"
                :ranges="isMobile ? false : defaultRanges"
                :opens="isMobile ? 'center' : 'right'"
                @toggle="dateRangeToggled"
                @update="dateRangeUpdate"
              ></date-range-picker>
              <div class="ranges">
                <div
                  class="range"
                  v-for="(range, name, i) in defaultRanges"
                  :key="i"
                >
                  <div
                    @click="
                      dateRangeUpdate({
                        startDate: range[0],
                        endDate: range[1],
                      })
                    "
                    :class="{ active: rangeSelected(range, dateRange) }"
                  >
                    <div
                      :class="{
                        'wrapper-of-rang-and-purple-circle': true,
                        selected: rangeSelected === name,
                      }"
                    >
                      <div
                        :class="{ 'purple-circle': rangeSelected === name }"
                      ></div>
                      {{ name }}
                    </div>
                  </div>
                  <div class="ul" v-if="rangeSelected(range, dateRange)" />
                </div>
              </div>
            </div>
            <Chart :data="chartDataFormatted" :metrics="chartMetrics" :summaryStats="summaryStats" />
          </div>
          <!-- Filter tabs -->
          <div class="filter-tabs">
            <button
              v-for="filter in filters"
              :key="filter"
              @click="currentFilter = filter"
              :class="{ active: currentFilter === filter }"
            >
              {{ filter }}
            </button>
          </div>
          <div class="sort">
            <div @click="$refs.select.click()" class="select">
              <select ref="select" v-model="sortBy" class="standard-select">
                <option v-for="(option, i) in sortOptions" :value="option.value" :key="i">
                  Sort by {{ option.label }}
                </option>
              </select>
              <ChevronDownIcon class="pointer-events-none" />
              <span class="focus"></span>
            </div>
          </div>
          <div class="link-cards">
            <div class="create-link-box">
              <div class="create-button">
                <button class="button-default big" @click="createLink = true">
                  <span>Create a New Smart Link</span>
                </button>
              </div>
            </div>
            <SmartLinkCard 
              v-for="link in filteredLinks" 
              :key="link.id" 
              :link="link" 
              :totals="linkTotals[link.id]" 
            />
          </div>
        </template>
        <div v-if="showNoLinks" class="no-links">
          <div class="box-content">
            <p class="heading">
              Stand out with premium links and landing pages
            </p>
            <p>
              Create links and landing pages for your artist profile, album,
              single, or playlist to market across email, social, and anywhere
              on the internet.
            </p>
            <p>
              When someone clicks on your link, they'll see a stunning landing
              page guiding them to their preferred destination to stream and buy
              your music. You even have the option to deeplink users directly
              into a destination, opening your content on apps like Spotify and
              Apple Music.
            </p>
            <div class="create-link-box">
              <button class="button-default big" @click="createLink = true">
                <span>Create a New Smart Link</span>
              </button>
            </div>
          </div>
          <div class="box-background">
            <SmartLinksFrameOne class="frame-1" />
          </div>
        </div>
      </div>
      <SnackBar>
        <template v-slot:icon>
          <CopyIcon />
        </template>
      </SnackBar>
      <CreateLinkWizard v-if="createLink" @close="close" @finish="finish" />
    </div>
  </div>
</template>

<script>
import CopyIcon from "../../../../public/img/icons/copy.svg";
import ChevronDownIcon from "../../../../public/img/icons/chevron-down.svg";
import SmartLinksFrameOne from "../../../../public/img/Smart-Links-Frame1_1.svg";
import { Q_LINKS, Q_LINK_TOTALS, Q_STATS_ARTIST } from "@/graphql";
import SnackBar from "@/components/Custom/SnackBar";
import CreateLinkWizard from "@/components/Custom/CreateLinkWizard";
import SmartLinkCard from "@/components/Custom/SmartLinkCard";
import Chart from "@/components/Custom/Chart.vue";
import moment from "moment";
import DateRangePicker from "vue2-daterange-picker";
import "vue2-daterange-picker/dist/vue2-daterange-picker.css";
import "@/assets/scss/md/_daterangepicker.scss";

export default {
  name: "SmartLinks",
  components: {
    CopyIcon,
    SmartLinksFrameOne,
    SmartLinkCard,
    SnackBar,
    CreateLinkWizard,
    ChevronDownIcon,
    Chart,
    DateRangePicker
  },
  data() {
    return {
      chartDataFormatted: {},
      chartMetrics: {},
      summaryStats: [],
      links: [],
      linkTotals: {},
      linksLoaded: false,
      createLink: false,
      sortBy: "newest",
      showDateRangePickerCalendar: false,
      dateRange: {
        startDate: moment()
          .subtract(1, "month")
          .toDate(),
        endDate: moment()
          .endOf("day")
          .toDate(),
      },
      defaultRanges: {
        "Last 7 Days": [
          moment()
            .subtract(7, "day")
            .toDate(),
          moment().toDate(),
        ],
        "Last 14 Days": [
          moment()
            .subtract(14, "day")
            .toDate(),
          moment().toDate(),
        ],
        "Last 30 Days": [
          moment()
            .subtract(30, "day")
            .toDate(),
          moment().toDate(),
        ],
        "Last 12 Months": [
          moment()
            .subtract(12, "month")
            .toDate(),
          moment().toDate(),
        ],
        Lifetime: [
          moment()
            .startOf("year")
            .toDate(),
          moment().toDate(),
        ],
      },
      sortOptions: [
        {
          value: "popular",
          label: "Most Popular First"
        },
        {
          value: "newest",
          label: "Newer First"
        },
        {
          value: "oldest",
          label: "Older First"
        }
      ],
      filters: ['All', 'Bio', 'Releases', 'Pre-Releases', 'Playlists'],
      currentFilter: 'All',
      chartData: [],
      chartDataLoaded: false
    };
  },
  apollo: {
    links: {
      query: Q_LINKS,
      variables() {
        return {
          artist_id: this.artist.id
        };
      },
      skip() {
        console.log("Q_LINKS skip", this.artist);
        return !this.artist.id;
      },
      result({ data }) {
        this.linksLoaded = true;
        console.log("links query", data);
      }
    },
    link_totals: {
      query: Q_LINK_TOTALS,
      variables() {
        return {
          artist_id: this.artist.id
        };
      },
      skip() {
        console.log("Q_LINK_TOTALS skip", this.artist);
        return !this.artist.id;
      },
      result({ data }) {
        data = JSON.parse(data.link_totals.data).reduce((obj, item) => {
          obj[item.link_id] = {
            clicks: item.clicks,
            views: item.views
          };
          return obj;
        }, {});
        console.log("link totals query", data);
        this.linkTotals = data;
      }
    },
    // New GQL query for artist statistics
    artist_statistics: {
      query: Q_STATS_ARTIST,
      variables() {
        return {
          artist_id: this.artist.id,
          start_time: this.dateRange.startDate,
          end_time: this.dateRange.endDate,
          offset: new Date().getTimezoneOffset().toString() // Example offset for timezone
        };
      },
      skip() {
        console.log("Q_STATS_ARTIST skip", this.artist);
        return !this.artist.id;
      },
      result({ data }) {
        this.chartData = [];
        if (data.artist_statistics) {
          // Process and store the chart data
          const events = data.artist_statistics.events; // Assuming events is an array
          if (!events.length) return;
          
          let days = {};
          let start = moment(this.dateRange.startDate);
          let end = moment(this.dateRange.endDate);
          let diff = end.diff(start, "days") + 1;

          // loop through days and push each day to this.chartData
          for (let i = 0; i < diff; i++) {
            let day = moment(this.dateRange.startDate)
              .add(i, "days")
              .format("YYYY-MM-DD");

            if (typeof days[day] == "undefined") {
              days[day] = {
                day: day,
                views: 0,
                clickthroughs: 0,
                subscribes: 0,
                presaves: 0,
              };
            }
          }

          for (const day in days) {
            this.chartData.push(days[day]);
          }

          for (let i = 0; i < data.artist_statistics.events.length; i++) {
            let record = data.artist_statistics.events[i];
            record.count = parseInt(record.count);

            let ds = record.day.slice(0, 10);
            if (typeof days[ds] == "undefined") continue;
            days[ds][record.event + "s"] = record.count;
          }

          this.chartDataLoaded = true;
          this.updateChart();
        }
      }
    },
  },
  async created() {
    console.log("created", this.artist);
    await this.$apollo.queries.links.refetch();
    if (!this.subscriptionActive(this.artist)) {
      // TODO: Re-enable after fixing subs
      // await this.$router.push({ name: "ArtistSettings" });
    } else {
      await this.$apollo.queries.links.refetch();
    }
  },
  computed: {
    artist() {
      return this.$store.getters.getArtist;
    },
    user() {
      return this.$store.getters.getUser;
    },
    showNoLinks() {
      return this.linksLoaded && !this.links.length;
    },
    sortedLinks() {
      if (this.sortBy === "newest") {
        return [...this.links].sort((a, b) => {
          return new Date(b.created_at) - new Date(a.created_at);
        });
      } else if (this.sortBy === "oldest") {
        return [...this.links].sort((a, b) => {
          return new Date(a.created_at) - new Date(b.created_at);
        });
      } else if (this.sortBy === "popular") {
        return [...this.links].sort((a, b) => {
          return b.views - a.views;
        });
      }
      return this.links;
    },
    filteredLinks() {
      if (this.currentFilter === 'All') {
        return this.sortedLinks;
      }
      return this.sortedLinks.filter(link => {
        // Adjust these conditions based on your link object structure
        switch(this.currentFilter) {
          case 'Bio':
            return link.type === 'bio';
          case 'Releases':
            return link.type === 'release';
          case 'Pre-Releases':
            return link.type === 'pre-release';
          case 'Playlists':
            return link.type === 'playlist';
          case 'Rewards':
            return link.type === 'reward';
          default:
            return true;
        }
      });
    },
    preReleasesWithoutRid() {
      return this.links.filter(link => link.type === 'pre-release' && !link.rid);
    },
    isMobile() {
      const toMatch = [
        /Android/i,
        /iPhone/i,
        /iPad/i,
        /iPod/i,
        /BlackBerry/i,
        /Windows Phone/i,
      ];

      return toMatch.some((toMatchItem) => {
        return navigator.userAgent.match(toMatchItem);
      });
    },
  },
  methods: {
    rangeSelected(range, dateRange) {
      return (
        moment(range[0]).isSame(dateRange.startDate) &&
        moment(range[1]).isSame(dateRange.endDate)
      );
    },
    dateRangeToggled(show) {
      this.showDateRangePickerCalendar = show;
    },
    dateRangeUpdate: function(range) {
      this.dateRange = range;
    },
    updateChart() {
      const chartMetrics = {
        Views: {
          definition: "The total number of views on the website.",
          color: "#9A2CCA",
        },
        Clicks: {
          definition: "The total number of clicks on the website's links.",
          color: "#A6CEE3",
        }
      };

      this.chartMetrics = chartMetrics;

      this.chartDataFormatted = {
        Views: this.chartData.map((x) => {
          return { date: x.day, value: x.views };
        }),
        Clicks: this.chartData.map((x) => {
          return { date: x.day, value: x.clickthroughs };
        })
      };

      const totalViews = this.chartData.reduce((acc, curr) => acc + curr.views, 0);

      if (totalViews > 0) {
        const totalClickthroughs = this.chartData.reduce((acc, curr) => acc + curr.clickthroughs, 0);
        const rate = (totalClickthroughs / totalViews * 100).toFixed(1);
        this.summaryStats = [
          {
            name: "CTR",
            value: `${rate}%`,
            color: "#FFFF00"
          }
        ];
      }
    },
    close() {
      this.createLink = false;
      this.$store.commit("resetCreateLinkWizardData");
    },
    async finish(link_id) {
      this.createLink = false;
      this.$store.commit("resetCreateLinkWizardData");
      await this.$router.push({
        name: "SmartLink",
        params: { id: link_id }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.filter-tabs {
  margin:10px;
  display: flex;
  justify-content: flex-start;
  margin-bottom: 20px;
  
  button {
    background: none;
    border: none;
    color: $text-type-1;
    font-size: 16px;
    padding: 10px 10px;
    cursor: pointer;
    transition: color 0.3s ease;
    
    &.active {
      color: white;
      border-bottom: 2px solid $purple-stroke;
    }
    
    &:hover {
      color: white;
    }
  }
}

.rid-missing {
  flex: 1;
  display: flex;
  background: #24224a;
  color: white;
  border-radius: 10px;
  padding: 12px 24px;
  margin-bottom: 20px;

  .text {
    display: flex;
    flex-direction: column;
    padding-right: 30px;

    .title {
      font-weight: 700;
      font-size: 16px;
      line-height: 26px;
      color: #ffffff;

      i {
        margin-right: 5px;
      }
    }

    .details {
      margin-top: 5px;
      font-weight: 400;
      font-size: 14px;
      line-height: 20px;
      color: #ffffff;
    }
  }

  .button-container {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: center;
    flex: 1;

    button {
      white-space: nowrap;
    }
  }
}

.apexcharts-gridline {
  stroke: rgba(177, 175, 205, 0.15);
}

g[seriesName="views"] > path {
  stroke: #9a2cca;
}

g[seriesName="clicks"] > path {
  stroke: #a6cee3;
}

g[seriesName="presaves"] > path {
  stroke: #1f78b4;
}

g[seriesName="subscribes"] > path {
  stroke: #ffff00;
}

.apexcharts-tooltip.apexcharts-active {
  background: #1b193c;
  border: none;
  border-radius: 5px;
  padding: 9px 14px;
}

.apexcharts-tooltip.apexcharts-theme-dark .apexcharts-tooltip-title {
  background: transparent;
  border: none;
  color: white;
  font-size: 16px;
  font-weight: bold;
  /*padding: 9px 6px;*/
  /*padding: 10px 17px;*/
  text-align: center;
}

.apexcharts-tooltip-text-label,
.apexcharts-tooltip-text-z-label {
  color: #6f6c99;
}

.apexcharts-tooltip-text-value,
.apexcharts-tooltip-text-z-value {
  color: white;
}


.link-cards {
  display: grid;
  grid-gap: 24px 30px;
  grid-template-columns: repeat(auto-fill, minmax(485px, auto));

  .create-link-box {
    width: 100%;
  }
}

.sort {
  margin-bottom: 15px;

  .select {
    width: fit-content !important;
    display: flex;
    margin-left: auto;

    label {
      margin-right: 10px;
      margin-top: 5px;
      width: fit-content;
    }

    select {
      width: 250px;
    }

    svg {
      right: 15px;
      top: 15px;
    }
  }
}
@media screen and (max-width: 512px){
    .no-links{
      display:block !important;
    }
}

.no-links {
  display: grid;
  grid-template-columns: repeat(2, minmax(495px, 1fr));
  grid-gap: 30px;
  position: relative;
  height: 100%;

  .box-background {
    display: flex;
    justify-content: center;
    align-items: center;

    .frame-1 {
      width: 200px;
      max-width: 200px;
    }

    .frame-2 {
      width: 540px;
      max-width: 540px;
    }
  }
}

.box-content {
  .heading {
    margin: 0;
    padding-bottom: 24px;
    font-weight: bold;
    font-size: 24px;
    line-height: 29px;
    color: white;
    mix-blend-mode: normal;
    opacity: 0.9;
  }

  p {
    margin: 0;
    padding-bottom: 24px;
    /* 18/Regular */
    font-size: 18px;
    line-height: 26px;
    color: white;
    mix-blend-mode: normal;
    opacity: 0.9;
  }
}

.create-link-box {
  display: flex;
  justify-content: center;
  padding: 47px 0;
  //width: fit-content;
  width: auto;
  /* BG/type1 */
  background: $bg-type-1;
  /* Purple-stroke */
  border: 1px dashed $purple-stroke;
  box-sizing: border-box;
  border-radius: 10px;

  .create-button {
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

/* Add scoped if you want the styles to be local to this component only */
.artists-stats-chart {
  max-width: 100%; /* Ensures the chart container doesn't exceed the width of its parent */
  margin: 20px 0; /* Add some margin around the chart for spacing */
  padding: 15px; /* Padding inside the chart container */
  background-color: #fff; /* Background color for the chart container */
  border-radius: 4px; /* Rounded corners for the chart container */
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1); /* A subtle shadow for some depth */
}

.wrapper-of-rang-and-purple-circle {
  display: flex;
  align-items: center;
  padding-left: 24px;
  transition: padding-left 0.3s ease;
  position: relative;
}

.wrapper-of-rang-and-purple-circle.selected {
  padding-left: 0;
}

.purple-circle {
  width: 9px;
  height: 4.5px;
  border-radius: 100px 100px 0 0;
  background-color: $purple;
  margin-right: 8px;
  transform: rotate(90deg);
  transform-origin: center;
}
.purple-circle::after {
  content: "";
  position: absolute;
  top: -3px;
  left: -3px;
  width: 150%;
  height: 150%;
  border-radius: 100px 100px 0 0;
  background: radial-gradient(
    circle at top right,
    rgba(157, 83, 232, 0.01) 1%,
    rgba(157, 83, 232, 0.31) 31%,
    rgba(157, 83, 232, 0.01) 99%
  );
  box-shadow: 0 0 10px rgba(157, 83, 232, 0.5);
  z-index: -1;
}
@media (max-width: 620px) {
  .content {
    padding: 36px 15px;
    width: 100%;
    margin: 0;
    .content-body {
      width: 100%;
      .sort {
        .select {
          margin-left: 0;
        }
      }
      
      .link-cards {
        margin-bottom: 40px;
        grid-template-columns: repeat(auto-fill, minmax(300px, auto));
        .create-link-box {
          width: 100%;
        }
      }
    }
  }
}
  .chart-date-picker {
    display: block;

    .ranges {
      display: none;
    }

    .vue-daterange-picker {
      min-width: auto;
      text-align: center;

      span {
        font-size: 10px;
      }
    }
  }

  .chart-card {
  margin-top: 18px;

  .card-top {
    display: flex;
    justify-content: space-between;

    .summary-stats {
      display: flex;

      .stat {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-width: 86px;
        min-height: 40px;
        margin-right: 24px;
        padding: 10px 17px;
        /* BG/type1 */
        background: $bg-type-1;
        border-radius: 10px;

        .title {
          /* 16/SemiBold */
          font-weight: 600;
          font-size: 16px;
          line-height: 24px;
          text-align: center;
          /* White */
          color: white;
        }

        .category {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 6px;

          .category-color {
            width: 6px;
            height: 6px;
            border-radius: 50%;
          }

          &.views {
            .category-color {
              background: #9a2cca;
            }
          }

          &.presaves {
            .category-color {
              background: #1f78b4;
            }
          }

          &.subscribes {
            .category-color {
              background: #ffff00;
            }
          }

          &.clickthroughs {
            .category-color {
              background: #a6cee3;
            }
          }

          .category-text {
            /* 12/Regular */
            font-size: 12px;
            line-height: 15px;
            /* Text/type1 */
            color: $text-type-1;
          }
        }
      }
    }
  }

  .chart {
    width: 100%;
    height: 200px;
    margin-top: 36px;
  }
}
.chart-card,
.sources-card,
.countries-card,
.destination-links-card {
  margin: 36px 0;
  /* BG/type2 */
  background: $bg-type-2;
  /* Shadow */
  box-shadow: 0px 50px 40px rgba(23, 18, 43, 0.3);
  border-radius: 10px;
  padding: 24px;

  .card-title {
    /* 18/SemiBold */
    font-weight: 600;
    font-size: 18px;
    line-height: 30px;
    /* White */
    color: white;
    mix-blend-mode: normal;
    opacity: 0.9;
  }
}
@media (max-width: 730px) {
  .vue-daterange-picker {
    ::v-deep {
      .daterangepicker {
        .calendar-table {
          td {
            min-width: 24px;
            height: 25px;
            line-height: 18px;
          }
          th {
            min-width: 24px;
          }
        } 
      } 
    }
  }
}
</style>